import { authResources } from "./auth-resources.js";
import { chatCitationResources } from "./chat-citation-resources.js";
import { chatContentResources } from "./chat-content-resources.js";
import { chatExecutionResources } from "./chat-execution-resources.js";
import { chatHistoryResources } from "./chat-history-resources.js";
import { chatInputResources } from "./chat-input-resources.js";
import { commonComponentsResources } from "./common-components-resources.js";
import { workbenchResources } from "./workbench-resources.js";
import type { WidenResourceValues } from "./resource-types.js";

const zhCNCoreResources = {
  common: {
    appTitle: "Tubo 智能体工作台",
    breadcrumb: "面包屑",
    more: "更多",
    featureUnavailable: "该功能尚未对当前租户开放",
    pageNotFound: "找不到此页面",
    pageNotFoundDescription: "此链接可能已失效，或你没有访问权限。",
    backToWorkbench: "返回工作台",
    language: "界面语言",
    chinese: "中文",
    english: "English",
    auto: "自动",
    save: "保存",
    cancel: "取消",
    retry: "重试",
    loading: "加载中…",
    failed: "操作失败",
    selectMembers: "选择成员",
    searchMembers: "搜索成员",
    noMatchingMembers: "未找到匹配的成员",
    treeViewer: {
      searchPlaceholder: "搜索…",
      items_one: "共 {{count}} 项",
      items_other: "共 {{count}} 项",
      folders_one: "{{count}} 个文件夹",
      folders_other: "{{count}} 个文件夹",
      documents_one: "{{count}} 个文档",
      documents_other: "{{count}} 个文档",
      expandAll: "展开全部",
      collapseAll: "折叠全部",
      noMatchingContent: "未找到匹配的内容",
      noData: "暂无数据",
    },
    error: {
      network: "网络连接失败",
      networkDescription: "{{context}}失败，请检查网络后重试",
      permission: "权限不足",
      permissionDescription: "你没有权限执行{{context}}操作",
      operationFailed: "操作失败，请稍后重试",
    },
    notSynced: "语言设置未同步，请稍后重试。",
    account: "我的账户",
    switchTeam: "切换团队",
    appearanceMode: "界面颜色模式",
    systemTheme: "跟随系统",
    dayMode: "日间模式",
    nightMode: "夜间模式",
    logout: "退出登录",
    user: "用户",
    plugin: "插件",
    expandSidebar: "展开侧边栏",
    collapseSidebar: "收起侧边栏",
    sharedComponents:
      commonComponentsResources["zh-CN"].common.sharedComponents,
  },
  navigation: {
    workbench: "工作台",
    knowledge: "知识库",
    documents: "文档中心",
    data: "数据中心",
    ontology: "知识本体",
    teamSettings: "团队设置",
    systemSettings: "系统设置",
    recycleBin: "回收站",
    dailyChem: "日化智控",
  },
  auth: {
    login: "登录",
    passwordLogin: "账号密码",
    phoneLogin: "手机号",
    usernameLabel: "用户名/邮箱/手机号",
    usernamePlaceholder: "请输入用户名/邮箱/手机号",
    passwordLabel: "密码",
    passwordPlaceholder: "请输入密码",
    showPassword: "显示密码",
    hidePassword: "隐藏密码",
    signIn: "登录",
    register: "注册",
    codeLabel: "验证码",
    codePlaceholder: "请输入验证码",
    getCode: "获取验证码",
    loginFailed: "登录失败，请稍后重试",
    loginContinue: "输入您的账号信息以继续",
    phoneContinue: "输入您的手机号以继续",
    phoneLabel: "手机号",
    phonePlaceholder: "请输入手机号",
    noAccount: "没有账号？",
    registration: "注册",
    smsFailed: "短信发送失败，请重试",
    privacyPolicy: "隐私协议",
    tagline: "国内首家依托业务本体建模\n支持本地化交付的产业级 AI 知识库平台",
    pageLoadFailed: "页面暂时无法加载，请重试。",
    retry: "重试",
    ...authResources["zh-CN"].auth,
  },
  chat: {
    responseLanguage: "回复语言",
    automatic: "自动跟随问题",
    documentOutputLanguage: "产物语言",
    documentLanguageAuto: "跟随本轮语言要求",
    required: "必填",
    optional: "选填",
    chooseOption: "请选择",
    yes: "是",
    no: "否",
    fillBySelf: "自己填写",
    chooseByName: "按名称选择",
    selectedExpand: "已选择，展开查看",
    searchName: "搜索名称…",
    searching: "正在查找…",
    candidatesFailed: "候选项加载失败",
    noMatchingOption: "没有可访问的匹配项，请换个名称搜索。",
    moreResults: "还有更多结果，请输入更完整的名称。",
    chinese: "中文",
    english: "English",
    send: "发送",
    stop: "停止",
    answerFailed: "回复生成失败，请稍后重试",
    languageAuto: "自动",
    ...chatInputResources["zh-CN"].chat,
    ...chatHistoryResources["zh-CN"].chat,
    ...chatCitationResources["zh-CN"].chat,
    ...chatContentResources["zh-CN"].chat,
  },
  execution: chatExecutionResources["zh-CN"].execution,
  workshop: {
    agentName: "智能体名称",
    englishName: "英文名称",
    description: "描述",
    englishDescription: "英文描述",
    callTitle: "工具调用",
    calls: "调用记录",
  },
  workbench: workbenchResources["zh-CN"].workbench,
} as const;

const enUSCoreResources: WidenResourceValues<typeof zhCNCoreResources> = {
  common: {
    appTitle: "Tubo Agent Workbench",
    breadcrumb: "Breadcrumb",
    more: "More",
    featureUnavailable: "This feature is not available for the current tenant.",
    pageNotFound: "Page not found",
    pageNotFoundDescription:
      "This link may be invalid, or you may not have access.",
    backToWorkbench: "Back to workbench",
    language: "Language",
    chinese: "中文",
    english: "English",
    auto: "Automatic",
    save: "Save",
    cancel: "Cancel",
    retry: "Retry",
    loading: "Loading…",
    failed: "Operation failed",
    selectMembers: "Select members",
    searchMembers: "Search members",
    noMatchingMembers: "No matching members found",
    treeViewer: {
      searchPlaceholder: "Search…",
      items_one: "{{count}} item",
      items_other: "{{count}} items",
      folders_one: "{{count}} folder",
      folders_other: "{{count}} folders",
      documents_one: "{{count}} document",
      documents_other: "{{count}} documents",
      expandAll: "Expand all",
      collapseAll: "Collapse all",
      noMatchingContent: "No matching content found",
      noData: "No data available",
    },
    error: {
      network: "Network connection failed",
      networkDescription:
        "Could not {{context}}. Check your connection and try again.",
      permission: "Insufficient permissions",
      permissionDescription: "You don't have permission to {{context}}.",
      operationFailed: "The operation failed. Please try again later.",
    },
    notSynced: "Your language preference was not saved. Please try again.",
    account: "My account",
    switchTeam: "Switch team",
    appearanceMode: "Appearance",
    systemTheme: "Follow system",
    dayMode: "Light mode",
    nightMode: "Dark mode",
    logout: "Sign out",
    user: "User",
    plugin: "Plugin",
    expandSidebar: "Expand sidebar",
    collapseSidebar: "Collapse sidebar",
    sharedComponents:
      commonComponentsResources["en-US"].common.sharedComponents,
  },
  navigation: {
    workbench: "Workbench",
    knowledge: "Knowledge base",
    documents: "Documents",
    data: "Data center",
    ontology: "Knowledge ontology",
    teamSettings: "Team settings",
    systemSettings: "System settings",
    recycleBin: "Recycle bin",
    dailyChem: "Daily Chem Operations",
  },
  auth: {
    login: "Sign in",
    passwordLogin: "Password",
    phoneLogin: "Phone",
    usernameLabel: "Username, email, or phone",
    usernamePlaceholder: "Enter your username, email, or phone",
    passwordLabel: "Password",
    passwordPlaceholder: "Enter your password",
    showPassword: "Show password",
    hidePassword: "Hide password",
    signIn: "Sign in",
    register: "Register",
    codeLabel: "Verification code",
    codePlaceholder: "Enter the verification code",
    getCode: "Get code",
    loginFailed: "Sign-in failed. Please try again.",
    loginContinue: "Enter your account information to continue",
    phoneContinue: "Enter your phone number to continue",
    phoneLabel: "Phone number",
    phonePlaceholder: "Enter your phone number",
    noAccount: "Don't have an account?",
    registration: "Register",
    smsFailed: "Could not send the SMS. Please try again.",
    privacyPolicy: "Privacy policy",
    tagline:
      "An industrial AI knowledge platform built on business ontology modeling and designed for on-premises delivery",
    pageLoadFailed: "The page could not be loaded. Please try again.",
    retry: "Retry",
    ...authResources["en-US"].auth,
  },
  chat: {
    responseLanguage: "Response language",
    automatic: "Follow the question",
    documentOutputLanguage: "Output language",
    documentLanguageAuto: "Follow this turn's language requirement",
    required: "Required",
    optional: "Optional",
    chooseOption: "Choose an option",
    yes: "Yes",
    no: "No",
    fillBySelf: "Enter a custom value",
    chooseByName: "Choose by name",
    selectedExpand: "Selected. Expand to view",
    searchName: "Search names…",
    searching: "Searching…",
    candidatesFailed: "Could not load options",
    noMatchingOption: "No accessible matches. Try another search.",
    moreResults: "More results are available. Enter a more specific name.",
    chinese: "中文",
    english: "English",
    send: "Send",
    stop: "Stop",
    answerFailed: "Could not generate a response. Please try again.",
    languageAuto: "Automatic",
    ...chatInputResources["en-US"].chat,
    ...chatHistoryResources["en-US"].chat,
    ...chatCitationResources["en-US"].chat,
    ...chatContentResources["en-US"].chat,
  },
  execution: chatExecutionResources["en-US"].execution,
  workshop: {
    agentName: "Agent name",
    englishName: "English name",
    description: "Description",
    englishDescription: "English description",
    callTitle: "Tool call",
    calls: "Calls",
  },
  workbench: workbenchResources["en-US"].workbench,
};

export const uiCoreResources = {
  "zh-CN": zhCNCoreResources,
  "en-US": enUSCoreResources,
} as const;
